import { useState, useEffect } from 'react';
import { AdminNavigation } from '../../components/AdminNavigation';
import { supabase } from '../../lib/supabase';
import { seed100Jobs, getEmployerForSeeding } from '../../lib/seed100Jobs';
import {
  Briefcase,
  Search,
  Filter,
  Eye,
  Edit,
  Trash2,
  Play,
  Pause,
  XCircle,
  Plus,
  Download,
  Check,
  AlertCircle,
} from 'lucide-react';

interface Job {
  id: string;
  title: string;
  company: string;
  location: string;
  remote: boolean;
  status: 'active' | 'paused' | 'closed';
  experience_level: string;
  salary_min: number | null;
  salary_max: number | null;
  created_at: string;
  employer_id: string;
  views_count: number;
}

export function JobsManagement() {
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    paused: 0,
    closed: 0,
  });

  useEffect(() => {
    loadJobs();
  }, [statusFilter]);

  const loadJobs = async () => {
    setLoading(true);
    try {
      let query = supabase
        .from('jobs')
        .select('*')
        .order('created_at', { ascending: false });

      if (statusFilter !== 'all') {
        query = query.eq('status', statusFilter);
      }

      const { data, error } = await query;

      if (error) throw error;

      setJobs(data || []);

      const totalJobs = data?.length || 0;
      const activeJobs = data?.filter((j) => j.status === 'active').length || 0;
      const pausedJobs = data?.filter((j) => j.status === 'paused').length || 0;
      const closedJobs = data?.filter((j) => j.status === 'closed').length || 0;

      setStats({
        total: totalJobs,
        active: activeJobs,
        paused: pausedJobs,
        closed: closedJobs,
      });
    } catch (error) {
      console.error('Error loading jobs:', error);
      setMessage({ type: 'error', text: 'Failed to load jobs' });
    } finally {
      setLoading(false);
    }
  };

  const handleSeed100Jobs = async () => {
    setSeeding(true);
    setMessage(null);

    try {
      const employerId = await getEmployerForSeeding();
      if (!employerId) {
        throw new Error('No employer found. Please create an employer account first.');
      }

      const result = await seed100Jobs(employerId);
      setMessage({
        type: 'success',
        text: result.message + `. Inserted: ${result.inserted}, Errors: ${result.errors}`,
      });

      await loadJobs();
    } catch (error: any) {
      setMessage({ type: 'error', text: error.message || 'Failed to seed jobs' });
    } finally {
      setSeeding(false);
    }
  };

  const handleUpdateStatus = async (jobId: string, newStatus: 'active' | 'paused' | 'closed') => {
    try {
      const { error } = await supabase
        .from('jobs')
        .update({ status: newStatus, updated_at: new Date().toISOString() })
        .eq('id', jobId);

      if (error) throw error;

      setMessage({ type: 'success', text: `Job status updated to ${newStatus}` });
      await loadJobs();
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to update job status' });
    }
  };

  const handleDeleteJob = async (jobId: string) => {
    if (!confirm('Are you sure you want to delete this job? This action cannot be undone.')) {
      return;
    }

    try {
      const { error } = await supabase.from('jobs').delete().eq('id', jobId);

      if (error) throw error;

      setMessage({ type: 'success', text: 'Job deleted successfully' });
      await loadJobs();
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to delete job' });
    }
  };

  const filteredJobs = jobs.filter(
    (job) =>
      job.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      job.company.toLowerCase().includes(searchQuery.toLowerCase()) ||
      job.location.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800 border-green-300';
      case 'paused':
        return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'closed':
        return 'bg-gray-100 text-gray-800 border-gray-300';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  return (
    <>
      <AdminNavigation />
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 ml-64">
        <div className="bg-gradient-to-r from-blue-600 to-cyan-600 text-white shadow-lg">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-4xl font-bold flex items-center gap-3">
                  <Briefcase className="w-10 h-10" />
                  Jobs Management
                </h1>
                <p className="text-blue-100 mt-2">Manage all platform jobs</p>
              </div>
              <button
                onClick={handleSeed100Jobs}
                disabled={seeding}
                className="px-6 py-3 bg-orange-500 hover:bg-orange-600 rounded-lg font-semibold transition flex items-center gap-2 disabled:opacity-50"
              >
                <Plus className="w-5 h-5" />
                {seeding ? 'Seeding...' : 'Seed 100 Jobs'}
              </button>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {message && (
            <div
              className={`mb-6 p-4 rounded-xl border-2 flex items-start gap-3 ${
                message.type === 'success'
                  ? 'bg-green-50 border-green-300 text-green-900'
                  : 'bg-red-50 border-red-300 text-red-900'
              }`}
            >
              {message.type === 'success' ? (
                <Check className="w-5 h-5 flex-shrink-0 mt-0.5" />
              ) : (
                <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
              )}
              <p className="font-semibold">{message.text}</p>
            </div>
          )}

          <div className="grid md:grid-cols-4 gap-6 mb-8">
            <div className="bg-white rounded-xl shadow-lg p-6 border-2 border-gray-200">
              <h3 className="font-bold text-gray-700 mb-2">Total Jobs</h3>
              <div className="text-3xl font-bold text-gray-900">{stats.total}</div>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-6 border-2 border-green-300">
              <h3 className="font-bold text-gray-700 mb-2">Active</h3>
              <div className="text-3xl font-bold text-green-600">{stats.active}</div>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-6 border-2 border-yellow-300">
              <h3 className="font-bold text-gray-700 mb-2">Paused</h3>
              <div className="text-3xl font-bold text-yellow-600">{stats.paused}</div>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-6 border-2 border-gray-300">
              <h3 className="font-bold text-gray-700 mb-2">Closed</h3>
              <div className="text-3xl font-bold text-gray-600">{stats.closed}</div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg border-2 border-gray-200 p-6">
            <div className="mb-6 flex gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search jobs..."
                  className="w-full pl-12 pr-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none"
                />
              </div>

              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="paused">Paused</option>
                <option value="closed">Closed</option>
              </select>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b-2 border-gray-200">
                      <th className="text-left py-3 px-4 font-bold text-gray-700">Title</th>
                      <th className="text-left py-3 px-4 font-bold text-gray-700">Company</th>
                      <th className="text-left py-3 px-4 font-bold text-gray-700">Location</th>
                      <th className="text-left py-3 px-4 font-bold text-gray-700">Status</th>
                      <th className="text-right py-3 px-4 font-bold text-gray-700">Salary</th>
                      <th className="text-center py-3 px-4 font-bold text-gray-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredJobs.map((job) => (
                      <tr key={job.id} className="border-b border-gray-100 hover:bg-gray-50 transition">
                        <td className="py-3 px-4">
                          <div className="font-semibold text-gray-900">{job.title}</div>
                          <div className="text-xs text-gray-600">{job.experience_level}</div>
                        </td>
                        <td className="py-3 px-4 text-gray-700">{job.company}</td>
                        <td className="py-3 px-4">
                          <div className="text-gray-700">{job.location}</div>
                          {job.remote && (
                            <div className="text-xs text-green-600 font-semibold">Remote</div>
                          )}
                        </td>
                        <td className="py-3 px-4">
                          <span
                            className={`px-3 py-1 rounded-full text-xs font-semibold border-2 ${getStatusColor(
                              job.status
                            )}`}
                          >
                            {job.status.charAt(0).toUpperCase() + job.status.slice(1)}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-right text-gray-700">
                          {job.salary_min && job.salary_max
                            ? `$${(job.salary_min / 1000).toFixed(0)}k - $${(job.salary_max / 1000).toFixed(0)}k`
                            : 'Not specified'}
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center justify-center gap-2">
                            {job.status !== 'active' && (
                              <button
                                onClick={() => handleUpdateStatus(job.id, 'active')}
                                className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition"
                                title="Activate"
                              >
                                <Play className="w-4 h-4" />
                              </button>
                            )}
                            {job.status !== 'paused' && (
                              <button
                                onClick={() => handleUpdateStatus(job.id, 'paused')}
                                className="p-2 text-yellow-600 hover:bg-yellow-50 rounded-lg transition"
                                title="Pause"
                              >
                                <Pause className="w-4 h-4" />
                              </button>
                            )}
                            {job.status !== 'closed' && (
                              <button
                                onClick={() => handleUpdateStatus(job.id, 'closed')}
                                className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition"
                                title="Close"
                              >
                                <XCircle className="w-4 h-4" />
                              </button>
                            )}
                            <button
                              onClick={() => handleDeleteJob(job.id)}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition"
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {filteredJobs.length === 0 && (
                  <div className="text-center py-12">
                    <Briefcase className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600 font-semibold">No jobs found</p>
                    <p className="text-gray-500 text-sm mt-1">Try adjusting your filters or seed some jobs</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
